/**
 * The SIRET check digit, mirrored from `Siret` on the backend.
 *
 * The backend stays the authority — it refuses the same numbers with the same
 * wording. This copy exists so a mistyped digit is caught under the field
 * instead of costing a round trip, exactly like `USERNAME_PATTERN` in
 * RegisterForm.
 */

export const SIRET_LENGTH = 14;

/** People copy these off paperwork, spaces and dots included. */
export function normaliseSiret(value) {
  return String(value ?? '')
    .trim()
    .replace(/[\s.-]/g, '');
}

/**
 * La Poste is the documented exception: its establishments do not satisfy
 * Luhn, and are recognised by a digit sum divisible by five.
 */
function isChecksumValid(digits) {
  const values = digits.split('').map(Number);

  if (digits.startsWith('356000000')) {
    return 0 === values.reduce((total, digit) => total + digit, 0) % 5;
  }

  const sum = values.reverse().reduce((total, digit, position) => {
    if (1 !== position % 2) {
      return total + digit;
    }

    const doubled = digit * 2;

    return total + (doubled > 9 ? doubled - 9 : doubled);
  }, 0);

  return 0 === sum % 10;
}

/**
 * What is wrong with this SIRET, or null when nothing is.
 *
 * An empty value is not a problem: a draft may have no SIRET at all, and it is
 * only required to submit the listing for review.
 */
export function siretProblem(value) {
  const digits = normaliseSiret(value);

  if ('' === digits) {
    return null;
  }

  if (!new RegExp(`^\\d{${SIRET_LENGTH}}$`).test(digits)) {
    return `Un SIRET compte ${SIRET_LENGTH} chiffres`;
  }

  if (!isChecksumValid(digits)) {
    return 'Ce SIRET est invalide : vérifiez les chiffres saisis';
  }

  return null;
}
